import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Sidebar from "../../../components/Sidebar";
import AppConstants from "../../../utils/appConstants";
import { getFaq } from "../../../utils/apiClient";
import "../../css/supportPages.css";

function parseFaqList(rawContent) {
  if (typeof rawContent !== "string") return [];
  try {
    const parsed = JSON.parse(rawContent);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((item) => ({
        question: item?.QUESTION || item?.question || item?.title || "",
        answer: item?.ANSWER || item?.answer || item?.content || "",
      }))
      .filter((item) => item.question || item.answer);
  } catch {
    return [];
  }
}

export default function SupportFaq() {
  const { t, i18n } = useTranslation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [faqItems, setFaqItems] = useState([]);
  const [openIndex, setOpenIndex] = useState(-1);

  useEffect(() => {
    try {
      const storedUser = sessionStorage.getItem("userData");
      if (storedUser) setUser(JSON.parse(storedUser));
    } catch (_) {}
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadFaq = async () => {
      setLoading(true);
      setError("");
      try {
        const languageCode = (i18n.language || "en").split("-")[0].toLowerCase();
        const userId =
          user?.USER_ID ||
          user?.user_id ||
          user?.ID ||
          user?.id ||
          localStorage.getItem("userId") ||
          sessionStorage.getItem("userId");

        const response = await getFaq(languageCode, { userId });
        const data = response?.decryptedData || response?.data || {};
        const code = Number(data?.CODE);
        if (code !== AppConstants.responseOk) {
          if (!cancelled) setError(data?.MESSAGE || "Failed to load FAQ content.");
          return;
        }

        const content = data?.DATA || [];
        if (!cancelled) {
          if (Array.isArray(content)) {
            setFaqItems(
              content
                .map((item) => ({
                  question: item?.QUESTION || item?.question || item?.title || "",
                  answer: item?.ANSWER || item?.answer || item?.content || "",
                }))
                .filter((item) => item.question || item.answer)
            );
          } else {
            setFaqItems(parseFaqList(content));
          }
        }
      } catch (err) {
        if (!cancelled) setError(err?.message || "Failed to load FAQ content.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadFaq();
    return () => {
      cancelled = true;
    };
  }, [i18n.language, user]);

  const handleToggle = (idx) => {
    setOpenIndex((prev) => (prev === idx ? -1 : idx));
  };

  return (
    <div className="support-page support-page--faq">
      <Sidebar user={user} />
      <div className="support-content support-content--faq">
        <h1 className="support-title support-title--faq">{t("faq", { ns: "mobile" })}</h1>
        <div className="support-container support-container--faq">
          {loading ? (
            <div className="support-faq-list" aria-busy="true">
              {Array.from({ length: 6 }).map((_, idx) => (
                <div key={`faq-skeleton-${idx}`} className="support-faq-item support-faq-item--skeleton">
                  <div className="support-faq-toggle">
                    <span
                      className="about-us-skeleton support-faq-skeleton--question"
                      style={{ width: `${58 + ((idx * 11) % 28)}%` }}
                      aria-hidden
                    />
                    <span className="about-us-skeleton support-faq-skeleton--chevron" aria-hidden />
                  </div>
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="support-error">{error}</div>
          ) : faqItems.length > 0 ? (
            <div className="support-faq-list">
              {faqItems.map((item, idx) => {
                const isOpen = openIndex === idx;
                return (
                  <div
                    key={`${item.question}-${idx}`}
                    className={`support-faq-item ${isOpen ? "is-open" : ""}`}
                  >
                    <button
                      type="button"
                      className="support-faq-toggle"
                      onClick={() => handleToggle(idx)}
                      aria-expanded={isOpen}
                    >
                      <span className="support-faq-question">
                        {item.question || t("question", { ns: "mobile" })}
                      </span>
                      <span className="support-faq-chevron" aria-hidden="true">
                        <i className={`fa-solid ${isOpen ? "fa-chevron-up" : "fa-chevron-down"}`} />
                      </span>
                    </button>
                    {isOpen ? (
                      <div className="support-faq-answer-wrap">
                        <p className="support-faq-answer">
                          {item.answer || t("no_answer_available", { ns: "mobile" })}
                        </p>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="support-empty">{t("no_content", { ns: "mobile" })}</div>
          )}
        </div>
      </div>
    </div>
  );
}
